import { UIElement } from '../../ui-element';
import styles from './ui-bottom-navigation-button.css';
import { Component } from '../../../decorators/component.decorator';
import { Child } from '../../../decorators/child.decorator';
import { Attribute } from '../../../decorators/attribute.decorator';
import { element } from '../../../global/template-literals';
import { dispatchMenuClick } from '../menu-click.helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIBottomNavigationButton
 * @element ui-bottom-navigation-button
 * @classdesc Represents a class for <code>ui-bottom-navigation-button</code> element.
 * Meant to be used in conjunction with <code>ui-bottom-navigation</code>
 * @property {string} [label] {@attr label}
 * @property {string} [href] {@attr href} Link URL.
 * Only when layout="link"
 * @property {UIBottomNavigationButtonLayout} [layout="link"] {@attr layout} Layout to use. Links take the user somewhere, buttons do something.
 * @property {string} [glyph] {@attr glyph} Glyph for link as a primary icon.
 * @property {boolean} [current] {@attr current} Whether this button represents the page the user is currently on.
 * @slot
 * @fires event:menu-click Dispatched when the button is clicked.
 * @example
 * <ui-bottom-navigation-button href="#" glyph="home" label="Overview"></ui-bottom-navigation-button>
 */
@Component({
    tag: 'ui-bottom-navigation-button',
    styles,
})
export class UIBottomNavigationButton extends UIElement {
    /**
     * @type {UIBottomNavigationButtonLayouts}
     * @readonly
     */
    static readonly Layout = {
        Link: 'link',
        Button: 'button',
    };

    @Attribute(String) glyph!: string;
    @Attribute(String) label!: string;
    @Attribute(String) href!: string;
    @Attribute(Boolean) current?: boolean;
    @Attribute(String) layout: string = UIBottomNavigationButton.Layout.Link;

    @Child(':scope > a') link?: HTMLAnchorElement;
    @Child(':scope > button') button?: HTMLButtonElement;
    @Child(':scope > ui-icon') icon!: HTMLElement;

    /** Returns true when the button is configured as a navigation link. */
    get isLink(): boolean {
        return this.layout === UIBottomNavigationButton.Layout.Link;
    }

    /**
     * Emits the shared menu-click event.
     * @fires event:menu-click
     * @private
     */
    private handleClick = (event: Event): void => {
        const interactive = this.link ?? this.button;
        if (!interactive?.contains(event.target as Node)) {
            return;
        }

        const prevented = dispatchMenuClick(this, this.href, event);
        if (prevented && this.isLink) {
            event.preventDefault();
        }
    };

    /**
     * @inheritDoc
     */
    render(): void {
        const icon = element`<ui-icon glyph="${this.glyph}"></ui-icon>`;

        const label = this.createElement({
            tagName: 'span',
            classList: {
                'ui-bottom-navigation-button__label': true,
            },
            children: [this.label],
        });

        const button = this.createElement({
            tagName: this.isLink ? 'a' : 'button',
            classList: {
                'ui-bottom-navigation-button__button': true,
            },
            attributes: {
                'href': this.isLink ? this.href : null,
                'aria-current': this.current ? 'page' : null,
            },
            children: [icon, label],
        });

        this.appendChild(button);
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.addEventListener('click', this.handleClick);
    }
}
